import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { fuseAnimations } from '@fuse/animations';
import { fromEvent, Observable, Subscription } from '../../../../../../node_modules/rxjs';
import { Subject, SubjectOrganisation } from '../../../../shared/models/subject';
import { SubjectLink } from '../../../../shared/models/subject-link';
import { SubjectType } from '../../../../shared/models/subject-type';
import { RelationshipService } from '../../../../shared/services/relationship.service';
import { SubjectTypeService } from '../../../../shared/services/subject-type.service';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { SubjectDialogComponent } from '../../subjects/subject-dialog/subject-dialog.component';
import { messageService } from 'app/shared/services/messages/message.module';
import { DataGridComponent } from 'app/components/data-grid/data-grid.component';
import { Utilities } from 'app/shared/helpers/utilities';
import { User } from 'app/shared/models/user';
import { environment } from 'environments/environment';
import { UserService } from 'app/shared/services/user.service';
import { SubjectService } from 'app/shared/services/subject.service';

@Component({
  selector: 'link-wizard',
  templateUrl: './link-wizard.component.html',
  styleUrls: ['./link-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class LinkWizardComponent implements OnDestroy, OnInit, AfterViewInit {

  existingRelationshipsFormGroup: UntypedFormGroup;

  btnSaveDisabled = false;

  subject: Subject;
  subjectType: SubjectType;
  parentSubjectType: SubjectType;

  linkSubject: Subject;
  addToDashboard: boolean;
  subjects: Subject[];
  columns = [];
  offset = 0;

  relationshipFormGroup: UntypedFormGroup;
  relationships = [];

  subjectLinks = [];  // hold list of subject links
  subjectsLinkedToLinkSubject = [];
  unrestrictedLinks = [];

  @ViewChild('search') filter: ElementRef;
  @ViewChild(DataGridComponent) dataGrid: DataGridComponent;
  @ViewChild('stepper') stepper: MatStepper;

  keyup: Subscription;

  constructor(
    public dialogRef: MatDialogRef<LinkWizardComponent>,
    private relationshipService: RelationshipService,
    private subjectTypeService: SubjectTypeService,
    private subjectService: SubjectService,
    private _formBuilder: UntypedFormBuilder,
    private userService: UserService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.subjectType = data.subjectType;
    this.subject = data.subject;
    this.parentSubjectType = data.parentSubjectType;
    if (this.subject.restricted) {
      this.unrestrictedLinks = this.subject.subjectLinks.filter((i) => !i.linkedSubject.restricted);
    }
  }

  ngOnInit() {

    // setup list of subjects
    this.columns = [{ name: 'Reference', prop: 'reference' }, { name: 'Summary', prop: 'summary' }];
    this.getListOfSubjects();

    // setup relationship from
    this.relationshipFormGroup = this._formBuilder.group({
      linkRelationship: new UntypedFormControl('', { validators: Validators.required, updateOn: 'blur' }),
      addToDashboard: true,
    });

    this.existingRelationshipsFormGroup = this._formBuilder.group({
      existingLinks: this._formBuilder.array([]),
    });
  }

  ngAfterViewInit(): void {
    Utilities.patchCheckboxAria();

    this.keyup = fromEvent(this.filter.nativeElement, 'keyup').pipe(
      debounceTime(150), distinctUntilChanged())
      .subscribe(() => {
        if (!this.subjects) {
          return;
        }
        this.applyFilter(this.filter.nativeElement.value);
      });
  }

  ngOnDestroy(): void {
    if (this.keyup) this.keyup.unsubscribe();
  }

  getListOfSubjects() {
    this.subjectService.getAllBySubjectType(this.subjectType.name, true).pipe(take(1)).subscribe((response) => {
      this.subjects = [...response].filter((i) => i.id !== this.subject.id).sort((a, b) => b.id - a.id);
    });
  }

  onSelectSubject(e) {
    const val = e.selectedRowKeys[0];
    this.linkSubject = this.subjects.find((i) => i.id === val);
  }

  createExistingLinkControl(subjectId: number): UntypedFormGroup {
    return this._formBuilder.group({
      linkedSubjectId: subjectId,
      directRelationshipId: undefined,
    });
  }

  // called when user clicks Next having selected subject to link
  setupRelationships() {
    // set available relationships to link the selected subject with the parent subject
    this.relationshipService.getRelationships(this.subject.subjectTypeId, this.subjectType.id).pipe(take(1)).subscribe((relationships) => {
      this.relationships = relationships;
    });

    // MRD - InCase Intelligence checks for existing subjects linked to the subject selected to be linked to the existing subject
    // PD requests this is inverted, so in fact we present the existing subjects linked to the existing subject...

    // get the subjects already linked to the selected subject
    this.subjectsLinkedToLinkSubject = this.subject.subjectLinks.filter((i) => i.linkedSubjectId !== this.linkSubject.id);

    // assign valid relationships to each element so we know what options to display...
    const existingLinks = this.existingRelationshipsFormGroup.get('existingLinks') as UntypedFormArray;
    while (existingLinks.length !== 0) {
      existingLinks.removeAt(0);
    }

    this.subjectsLinkedToLinkSubject.map((subjectlink) => {
      // create corresponding control
      existingLinks.push(this.createExistingLinkControl(subjectlink.linkedSubject.id));

      // get available relationships for the linked subject
      this.relationshipService.getRelationships(subjectlink.linkedSubject.subjectTypeId, this.linkSubject.subjectTypeId).pipe(take(1)).subscribe((relationships) => {
        subjectlink.relationships = relationships;
      });

      // get the subject type for the linked subject
      this.subjectTypeService.getById(subjectlink.linkedSubject.subjectTypeId).pipe(take(1)).subscribe((subjectType) => {
        subjectlink.subjectType = subjectType;
      });
    });
  }

  saveLinks() {
    if (this.btnSaveDisabled == true)
      return;
    this.btnSaveDisabled = true;

    const subjectLink = new SubjectLink();
    // subjectLink.subjectId = this.subject.id;
    subjectLink.relationshipId = this.relationshipFormGroup.get('linkRelationship').value;
    subjectLink.linkedSubjectId = this.linkSubject.id;
    this.subject.subjectLinks.push(subjectLink);

    // get state of add to dashboard...
    const raw = this.relationshipFormGroup.getRawValue();
    this.addToDashboard = raw.addToDashboard;

    // get other relationships and save as direct link, if specified
    const otherRelationships = this.existingRelationshipsFormGroup.getRawValue();
    otherRelationships.existingLinks.map((existingLink) => {
      if (existingLink && existingLink.directRelationshipId) {
        const target = this.subject.subjectLinks.find((i) => i.linkedSubjectId === existingLink.linkedSubjectId);
        if (target) {
          const sl = new SubjectLink();
          sl.relationshipId = existingLink.directRelationshipId;
          sl.linkedSubjectId = this.linkSubject.id;
          sl.subjectId = target.linkedSubject.id;
          this.subjectService.addLink(sl).pipe(take(1)).subscribe();
        }
      }
    });

    // setup subscriber to close form once subject has been successfully saved...
    this.subjectService.onAfterSave.pipe(take(1)).subscribe((subject) => {
      subjectLink.linkedSubject = this.linkSubject;
      this.dialogRef.close({
        data: {
          subject,
          subjectLink,
          addToDashboard: this.addToDashboard,
        },
      });

      if (this.unrestrictedLinks.length > 0) {
        this.unrestrictedLinks.forEach((i) => {
          this.showRestrictionsHaveBeenUpdatedToastMessage(i.linkedSubject);
        });
      }
      if (this.subject.restricted) {
        this.showRestrictionsHaveBeenUpdatedToastMessage(this.linkSubject);
      }
    });

    // ...save changes
    this.subjectService.save(this.subject, 'Link saved');
  }

  showRestrictionsHaveBeenUpdatedToastMessage(subject: Subject) {
    if ((!subject.subjectType.primary) || (!subject.restricted)) {
      messageService.info(`Security restrictions have been updated for ${subject.summary}.`, 'Restrictions updated.');
    }
  }

  applyFilter(value) {
    this.dataGrid.instance.searchByText(value);
  }

  addSubject() {
    const subject = new Subject();
    subject.isReferral = this.subject.isReferral;
    subject.subjectTypeId = this.subjectType.id;

    if (environment.limitAccessToSubjectsByOrg === true) {
      var me: User = this.userService.getCurrentUser();
      if (me.organisationId > 0) {
        var subjectOrg: SubjectOrganisation = new SubjectOrganisation();
        subjectOrg.organisationId = me.organisationId;
        subject.subjectOrganisations.push(subjectOrg);
      }
    }

    this.dialog.open(SubjectDialogComponent, {
      data: {
        subject,
        subjectType: this.subjectType,
      },
    }).afterClosed().subscribe((result) => {
      if (result && result.subject) {
        this.linkSubject = result.subject;
        this.setupRelationships();
        setTimeout(() => {
          this.stepper.next();
        }, 1);
      }
    });
  }
}
